import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useTenantStore } from '@/stores/tenantStore'
import { useThemeStore } from '@/stores/themeStore'
import { apiClient, getToken, handleApiResponse } from '@/services/api'

// Layout components
import AuthLayout from '@/components/layouts/AuthLayout'
import AppLayout from '@/components/layouts/AppLayout'

// Page components
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const PrintersPage = lazy(() => import('@/pages/PrintersPage'))
const AddPrinterPage = lazy(() => import('@/pages/AddPrinterPage'))
const JobsPage = lazy(() => import('@/pages/JobsPage'))
const JobDetailPage = lazy(() => import('@/pages/JobDetailPage'))
const CreateJobPage = lazy(() => import('@/pages/CreateJobPage'))
const QuickPrintPage = lazy(() => import('@/pages/QuickPrintPage'))
const TemplatesPage = lazy(() => import('@/pages/TemplatesPage'))
const CreateTemplatePage = lazy(() => import('@/pages/CreateTemplatePage'))
const EditTemplatePage = lazy(() => import('@/pages/EditTemplatePage'))
const DevicesPage = lazy(() => import('@/pages/DevicesPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const HelpPage = lazy(() => import('@/pages/HelpPage'))
const KIBonPage = lazy(() => import('@/pages/KIBonPage'))
const ChallengePage = lazy(() => import('@/pages/ChallengePage'))
const StoryChallengePage = lazy(() => import('@/pages/StoryChallengePage'))
const StoryLibraryPage = lazy(() => import('@/pages/StoryLibraryPage'))
const OTAUpdatesPage = lazy(() => import('@/pages/OTAUpdatesPage'))
const AdminEmailPage = lazy(() => import('@/pages/AdminEmailPage'))
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'))
const AdminKIBonPage = lazy(() => import('@/pages/AdminKIBonPage'))
const AdminI18nPage = lazy(() => import('@/pages/AdminI18nPage'))
const TenantManagementPage = lazy(() => import('@/pages/TenantManagementPage'))
const TenantMembersPage = lazy(() => import('@/pages/TenantMembersPage'))
const TenantThemeSettingsPage = lazy(() => import('@/pages/admin/TenantThemeSettingsPage'))
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'))
const WidgetsPage = lazy(() => import('@/pages/WidgetsPage'))
const StandaloneWidgetsPage = lazy(() => import('@/pages/StandaloneWidgetsPage'))
const DateTimeWidgetPage = lazy(() => import('@/pages/widgets/DateTimeWidgetPage'))
const QuotesWidgetPage = lazy(() => import('@/pages/widgets/QuotesWidgetPage'))
const WeatherWidgetPage = lazy(() => import('@/pages/widgets/WeatherWidgetPage'))
const AstronomyWidgetPage = lazy(() => import('@/pages/widgets/AstronomyWidgetPage'))
const SudokuWidgetPage = lazy(() => import('@/pages/widgets/SudokuWidgetPage'))
const StocksWidgetPage = lazy(() => import('@/pages/widgets/StocksWidgetPage'))
const WidgetTemplatesPage = lazy(() => import('@/pages/widgets/WidgetTemplatesPage'))
const MorningBriefingPage = lazy(() => import('@/pages/MorningBriefingPage'))
const FeatureFlagsAdminPage = lazy(() => import('@/pages/FeatureFlagsAdminPage'))
const PrivacyCenterPage = lazy(() => import('@/pages/PrivacyCenterPage'))
const AdminGDPRPage = lazy(() => import('@/pages/AdminGDPRPage'))
const AdminTaskTemplatesPage = lazy(() => import('@/pages/AdminTaskTemplatesPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// Mission System Pages
const MissionOnboardingPage = lazy(() => import('@/pages/missions/MissionOnboardingPage'))
const MissionBrowserPage = lazy(() => import('@/pages/missions/MissionBrowserPage'))
const RunningMissionsPage = lazy(() => import('@/pages/missions/RunningMissionsPage'))
const MissionBuilderPage = lazy(() => import('@/pages/missions/MissionBuilderPage'))
const MissionSetupPage = lazy(() => import('@/pages/missions/MissionSetupPage'))
const MissionFeedbackPage = lazy(() => import('@/pages/missions/MissionFeedbackPage'))
const MissionMonitorPage = lazy(() => import('@/pages/missions/MissionMonitorPage'))
const MissionPlayPage = lazy(() => import('@/pages/missions/MissionPlayPage'))

// Protected route component
import ProtectedRoute from '@/components/auth/ProtectedRoute'

// Common components
import OfflineIndicator from '@/components/common/OfflineIndicator'
import PWAInstallBanner from '@/components/common/PWAInstallBanner'

function App() {
  const { isAuthenticated, user, logout, isSessionExpired } = useAuthStore()
  const { currentTenant, fetchTenants } = useTenantStore()
  const { loadTheme, clearTheme } = useThemeStore()

  useEffect(() => {
    // Check for existing token and session validity
    const useCookie = ((import.meta.env.VITE_USE_COOKIE_TOKENS || 'false').toLowerCase() === 'true')
    if (!useCookie) {
      const token = getToken()
      if (token && isAuthenticated) {
        if (isSessionExpired()) {
          logout()
        }
      } else if (!token && isAuthenticated) {
        logout()
      }
    } else {
      // Cookie-based auth: try to hydrate the session from the server if not authenticated yet
      if (!isAuthenticated) {
        apiClient.get('/auth/me')
          .then((res) => {
            const user = handleApiResponse(res)
            if (user && (user as any).id) {
              // Use store directly to avoid circular imports
              // @ts-ignore
              useAuthStore.getState().login(user)
            }
          })
          .catch(() => {
            // ignore — user not logged in
          })
      }
    }
  }, [isAuthenticated, isSessionExpired, logout])

  // Load tenants when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchTenants()
    }
  }, [isAuthenticated, user, fetchTenants])

  // Load theme when tenant changes or on initial load
  useEffect(() => {
    if (isAuthenticated && currentTenant) {
      console.log(`[App] Loading theme for tenant ${currentTenant.id}...`)
      loadTheme(currentTenant.id)
    } else if (!isAuthenticated) {
      // Clear theme on logout
      console.log('[App] Clearing theme on logout')
      clearTheme()
    }
  }, [isAuthenticated, currentTenant, loadTheme, clearTheme])

  // Onboarding disabled

  return (
    <div className="App">
      <OfflineIndicator />
      <PWAInstallBanner />
      <Suspense fallback={<div style={{padding: 24}}>Loading…</div>}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={
          !isAuthenticated ? (
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          ) : (
            <Navigate to="/dashboard" replace />
          )
        } />
        
        <Route path="/register" element={
          !isAuthenticated ? (
            <AuthLayout>
              <RegisterPage />
            </AuthLayout>
          ) : (
            <Navigate to="/dashboard" replace />
          )
        } />


        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/printers" element={
          <ProtectedRoute>
            <AppLayout>
              <PrintersPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/printers/add" element={
          <ProtectedRoute>
            <AppLayout>
              <AddPrinterPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/printers/:id/settings" element={
          <ProtectedRoute>
            <AppLayout>
              <AddPrinterPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/jobs" element={
          <ProtectedRoute>
            <AppLayout>
              <JobsPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/jobs/:jobId" element={
          <ProtectedRoute>
            <AppLayout>
              <JobDetailPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/jobs/create" element={
          <ProtectedRoute>
            <AppLayout>
              <CreateJobPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/jobs/quick" element={
          <ProtectedRoute>
            <AppLayout>
              <QuickPrintPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/templates" element={
          <ProtectedRoute>
            <AppLayout>
              <TemplatesPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/templates/:id/edit" element={
          <ProtectedRoute>
            <AppLayout>
              <EditTemplatePage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/templates/create" element={
          <ProtectedRoute>
            <AppLayout>
              <CreateTemplatePage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/templates/:id/edit" element={
          <ProtectedRoute>
            <AppLayout>
              <EditTemplatePage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/templates/create" element={
          <ProtectedRoute>
            <AppLayout>
              <CreateTemplatePage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/ki-bon" element={
          <ProtectedRoute>
            <AppLayout>
              <KIBonPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/challenges" element={
          <ProtectedRoute>
            <AppLayout>
              <ChallengePage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/story-challenges" element={
          <ProtectedRoute>
            <AppLayout>
              <StoryChallengePage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/story-library" element={
          <ProtectedRoute>
            <AppLayout>
              <StoryLibraryPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/devices" element={
          <ProtectedRoute>
            <AppLayout>
              <DevicesPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Redirect /widgets to /widgets/templates */}
        <Route path="/widgets" element={<Navigate to="/widgets/templates" replace />} />

        <Route path="/widgets/datetime" element={
          <ProtectedRoute>
            <AppLayout>
              <DateTimeWidgetPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/widgets/quotes" element={
          <ProtectedRoute>
            <AppLayout>
              <QuotesWidgetPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/widgets/weather" element={
          <ProtectedRoute>
            <AppLayout>
              <WeatherWidgetPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/widgets/astronomy" element={
          <ProtectedRoute>
            <AppLayout>
              <AstronomyWidgetPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/widgets/sudoku" element={
          <ProtectedRoute>
            <AppLayout>
              <SudokuWidgetPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/widgets/stocks" element={
          <ProtectedRoute>
            <AppLayout>
              <StocksWidgetPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/widgets/templates" element={
          <ProtectedRoute>
            <AppLayout>
              <WidgetTemplatesPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/widgets/legacy" element={
          <ProtectedRoute>
            <AppLayout>
              <WidgetsPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/standalone-widgets" element={
          <ProtectedRoute>
            <AppLayout>
              <StandaloneWidgetsPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/morning-briefing" element={
          <ProtectedRoute>
            <AppLayout>
              <MorningBriefingPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/ota-updates" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <OTAUpdatesPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/users" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <AdminUsersPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/tenants" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <TenantManagementPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/tenants/:tenantId/members" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <TenantMembersPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/tenants/:tenantId/theme" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <TenantThemeSettingsPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/i18n" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <AdminI18nPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/email" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <AdminEmailPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/ki-bon" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <AdminKIBonPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/feature-flags" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <FeatureFlagsAdminPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/gdpr" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <AdminGDPRPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/task-templates" element={
          <ProtectedRoute requireRole="admin">
            <AppLayout>
              <AdminTaskTemplatesPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/privacy" element={
          <ProtectedRoute>
            <AppLayout>
              <PrivacyCenterPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/help" element={
          <ProtectedRoute>
            <AppLayout>
              <HelpPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } />

        {/* Mission System Routes */}
        <Route path="/missions/onboarding" element={
          <ProtectedRoute>
            <AppLayout>
              <MissionOnboardingPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/missions/browse" element={
          <ProtectedRoute>
            <AppLayout>
              <MissionBrowserPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/missions/community" element={
          <ProtectedRoute>
            <AppLayout>
              <MissionBrowserPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/missions/running" element={
          <ProtectedRoute>
            <AppLayout>
              <RunningMissionsPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/missions/builder" element={
          <ProtectedRoute>
            <MissionBuilderPage />
          </ProtectedRoute>
        } />

        <Route path="/missions/builder/:id" element={
          <ProtectedRoute>
            <MissionBuilderPage />
          </ProtectedRoute>
        } />

        <Route path="/missions/setup/:templateId" element={
          <ProtectedRoute>
            <AppLayout>
              <MissionSetupPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Public Mission Feedback (no auth, no layout) - Global page for all players */}
        <Route path="/missions/feedback/:token" element={
          <MissionFeedbackPage />
        } />
        <Route path="/missions/feedback/:token/:playerName" element={
          <MissionFeedbackPage />
        } />

        <Route path="/missions/monitor/:instanceId" element={
          <ProtectedRoute>
            <AppLayout>
              <MissionMonitorPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Mission Play Page - Minimal player UI for choices and collaborative tasks */}
        <Route path="/missions/play/:instanceId" element={
          <ProtectedRoute>
            <MissionPlayPage />
          </ProtectedRoute>
        } />

        {/* Default redirects */}
        <Route path="/" element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
        } />

        {/* 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </div>
  )
}

export default App
